import React, { useEffect, useState } from "react";
import { Clock, RefreshCw, Repeat } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

// Tier → cost mapping must mirror RENEWAL_STORAGE_COSTS in
// backend/video_lifecycle.py.
const TIERS = [
  { days: 10, cost: 0.0 },
  { days: 20, cost: 1.0 },
  { days: 30, cost: 2.0 },
  { days: 40, cost: 3.0 },
];

/**
 * VideoRenewalDialog — modal owned by MyVideosTab that lets the producer
 * pick a renewal window (10/20/30/40 days), toggle Auto-Renew, and posts
 * to `/videos/{id}/renew` + `/videos/{id}/auto-renew`. Parent decides
 * when this is open via the `video` prop (truthy = open). On success we
 * surface the new expires_at via `onRenewed` so MyVideosTab can update
 * its row in place.
 */
const VideoRenewalDialog = ({ video, onClose, onRenewed, userCredits }) => {
  const [selected, setSelected] = useState(10);
  const [autoRenew, setAutoRenew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);

  // Reset selection state whenever the dialog opens onto a new video.
  useEffect(() => {
    if (video) {
      setSelected(10);
      setAutoRenew(!!video.auto_renew);
    }
  }, [video]);

  if (!video) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/videos/${video.id}/renew`, { storage_days: selected });
      toast.success(`Renewed — new expiry ${new Date(res.data.expires_at).toLocaleDateString()}`);
      onRenewed?.({
        videoId: video.id,
        expires_at: res.data.expires_at,
        storage_days: res.data.storage_days,
        new_balance: res.data.new_balance,
      });
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't renew video");
    }
    setSubmitting(false);
  };

  const toggleAuto = async (next) => {
    setSavingAuto(true);
    try {
      await api.post(`/videos/${video.id}/auto-renew`, { enabled: next });
      setAutoRenew(next);
      toast.success(next ? "Auto-renew on — we'll keep this video alive for free" : "Auto-renew off");
      onRenewed?.({ videoId: video.id, auto_renew: next });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update auto-renew");
    }
    setSavingAuto(false);
  };

  const selectedTier = TIERS.find(t => t.days === selected) || TIERS[0];
  const canAfford = (userCredits ?? 0) >= selectedTier.cost;

  return (
    <Dialog open={!!video} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white" data-testid="video-renewal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            <Clock className="h-5 w-5" /> Renew "{video.title}"
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Keep this video live in the feed for more days. Time stacks on top of
            whatever is left, so renewing early never wastes the remainder.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mt-2">
          {TIERS.map((t) => {
            const affordable = (userCredits ?? 0) >= t.cost;
            return (
              <button
                key={t.days}
                type="button"
                onClick={() => setSelected(t.days)}
                disabled={!affordable}
                data-testid={`renew-tier-${t.days}`}
                className={`p-3 rounded-lg border text-left transition-all ${
                  selected === t.days
                    ? 'bg-emerald-600/20 border-emerald-500 text-white'
                    : affordable
                      ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                      : 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed'
                }`}
                title={!affordable ? `Need ${t.cost.toFixed(2)} credits — top up` : ''}
              >
                <div className="font-semibold text-sm">+{t.days} days</div>
                <div className="text-[11px] opacity-75">
                  {t.cost === 0 ? 'Free' : `${t.cost.toFixed(2)} credits`}
                </div>
              </button>
            );
          })}
        </div>

        {/* Auto-renew toggle — independent of the manual renewal. Once
            enabled, the daily expire-cron extends the video by the free
            10-day tier instead of moving it to the Expired Marketplace. */}
        <button
          type="button"
          onClick={() => toggleAuto(!autoRenew)}
          disabled={savingAuto}
          data-testid="renew-auto-toggle"
          className={`mt-3 w-full flex items-center justify-between gap-2 p-3 rounded-lg border transition-all text-left ${
            autoRenew
              ? 'bg-cyan-600/20 border-cyan-500/60 text-white'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
          }`}
        >
          <span className="flex items-center gap-2 text-sm">
            <Repeat className={`h-4 w-4 ${autoRenew ? 'text-cyan-300' : 'text-gray-500'}`} />
            <span className="font-semibold">Auto-renew this video</span>
          </span>
          <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${
            autoRenew ? 'bg-cyan-500 text-gray-900' : 'bg-gray-700 text-gray-400'
          }`}>
            {autoRenew ? 'ON' : 'OFF'}
          </span>
        </button>
        <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
          When on, GlobalEyes extends this video by 10 free days every cycle
          so it never lapses. Turn off any time to let it head to the Expired
          Marketplace at end of life.
        </p>

        <DialogFooter className="mt-4 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid="renew-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || !canAfford}
            data-testid="renew-confirm-btn"
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${submitting ? 'animate-spin' : ''}`} />
            {submitting ? 'Renewing…' : `Renew (${selectedTier.cost.toFixed(2)} cr)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoRenewalDialog;
